'use client'

import React, { FC, ReactElement } from 'react'
import styles from './InscriptionWarningExcessSats.module.css'
import WarningModal from '@/components/warning-modal'
import classNames from 'classnames'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useSatoshiPriceUSD from '@/utils/hooks/useSatoshiPriceUSD'
import { NETWORK } from '@/utils/constants'

const SATS_UPPER_LIMIT = 500000

const InscriptionWarningExcessSats: FC<{
	open: boolean
	action: 'transfer' | 'sell' | 'warning'
	onClose: () => void
	onConfirm: () => void
	totalSats: number
}> = (props): ReactElement => {
	const { action, onConfirm, onClose, totalSats } = props
	const price = useSatoshiPrice(totalSats)
	const [priceUSD] = useSatoshiPriceUSD(totalSats)

	const { description, confirmLabel, cancelLabel, variant } = React.useMemo(() => {
		let variant = 'confirm'
		let confirmLabel = 'I still want to transfer'
		let cancelLabel = 'Cancel transfer'
		let description = (
			<p>
				Your inscription contains{' '}
				<span className={classNames(styles.sats, 'bold')}>
					{price} {NETWORK} (${priceUSD})
				</span>{' '}
				in it! If you sell or transfer it, it will send the item with {price} {NETWORK} (${priceUSD}
				).
			</p>
		)

		if (action === 'sell' && totalSats < SATS_UPPER_LIMIT) {
			confirmLabel = 'I still want to list it'
			cancelLabel = 'Cancel sale'
		} else if (action === 'sell') {
			variant = 'ack'
			confirmLabel = 'I understand'
			description = (
				<p>
					Your inscription contains{' '}
					<span className={classNames(styles.sats, 'bold')}>
						{price} {NETWORK} (${priceUSD})
					</span>{' '}
					in it! You will need to reduce the Satoshis in this ordinal to list it for sale on our
					marketplace.
				</p>
			)
		}

		return { description, confirmLabel, cancelLabel, variant }
	}, [totalSats, action, price, priceUSD])

	const handleConfirm = React.useCallback(() => {
		if (variant === 'confirm') {
			onConfirm()
		} else {
			onClose()
		}
	}, [onConfirm, variant, onClose])

	return (
		<WarningModal
			open={props.open}
			onClose={onClose}
			title="Inscription contains too many sats!"
			description={description}
			confirmLabel={confirmLabel}
			cancelLabel={cancelLabel}
			onConfirm={handleConfirm}
			onCancel={onClose}
			buttonDisplay={variant === 'ack' ? 'confirm-only' : 'inverted'}
			icon="alert"
		></WarningModal>
	)
}

export default InscriptionWarningExcessSats
